import {
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
  Input,
  TextField,
} from '@fold-and-flow/ui'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'
import { trpc } from '#lib/trpc'

interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'client'
  createdAt: Date
}

const columnHelper = createColumnHelper<User>()

export function AdminUsersPage() {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const { data: users = [], refetch } = trpc.admin.listUsers.useQuery()
  const promote = trpc.admin.promoteToAdmin.useMutation({ onSuccess: () => refetch() })
  const demote = trpc.admin.demoteToClient.useMutation({ onSuccess: () => refetch() })

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (info) => <span className="font-medium text-warm-gray-800">{info.getValue()}</span>,
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => <span className="text-warm-gray-600">{info.getValue()}</span>,
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: (info) => {
        const role = info.getValue()
        return (
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              role === 'admin'
                ? 'bg-terracotta-400/10 text-terracotta-600'
                : 'bg-cream-200 text-warm-gray-600'
            }`}
          >
            {role}
          </span>
        )
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Joined',
      cell: (info) => (
        <span className="text-warm-gray-400 text-xs">{info.getValue().toLocaleDateString()}</span>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const { id, role } = row.original
        return role === 'client' ? (
          <Button
            variant="ghost"
            size="sm"
            loading={promote.isPending}
            onClick={() => promote.mutate({ userId: id })}
          >
            Make admin
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            loading={demote.isPending}
            onClick={() => demote.mutate({ userId: id })}
          >
            Demote
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: users,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-warm-gray-900">Users</h1>
          <p className="text-warm-gray-400 mt-1">{users.length} accounts</p>
        </div>
        <InviteUserDialog onSuccess={() => refetch()} />
      </div>

      <Input
        placeholder="Search by name or email…"
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        className="max-w-xs"
      />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-cream-200">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-xs font-medium text-warm-gray-400 uppercase tracking-wide cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          header.column.getToggleSortingHandler()?.(e)
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc'
                        ? ' ↑'
                        : header.column.getIsSorted() === 'desc'
                          ? ' ↓'
                          : ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-cream-100 hover:bg-cream-50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-6 py-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function InviteUserDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  const invite = trpc.admin.inviteUser.useMutation({
    onSuccess: () => {
      onSuccess()
    },
  })

  function handleOpen(isOpen: boolean) {
    setOpen(isOpen)
    if (!isOpen) {
      invite.reset()
      setName('')
      setEmail('')
    }
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault()
    invite.mutate({ name, email })
  }

  const errorMessage = invite.error instanceof Error ? invite.error.message : null

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger render={<Button variant="primary">Invite user</Button>} />
      <DialogPopup>
        <DialogClose
          aria-label="Close"
          className="absolute right-5 top-5 text-warm-gray-400 hover:text-warm-gray-600 p-1 rounded-xl hover:bg-cream-100 transition-colors"
        >
          ✕
        </DialogClose>
        <DialogTitle>Invite a new client</DialogTitle>

        {invite.isSuccess ? (
          <div className="flex flex-col gap-4 mt-4">
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              Invite sent to <strong>{email}</strong>. They'll receive an email with a link to set
              their password.
            </div>
            <Button variant="secondary" onClick={() => handleOpen(false)}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
            {errorMessage ? (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                {errorMessage}
              </div>
            ) : null}
            <TextField
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              required
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              required
            />
            <div className="flex gap-3 justify-end pt-2">
              <Button type="button" variant="secondary" onClick={() => handleOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={invite.isPending}>
                Send invite
              </Button>
            </div>
          </form>
        )}
      </DialogPopup>
    </Dialog>
  )
}
